import React, { useState } from 'react';
import PageHero from '../../components/public/PageHero';
import PublicLayout from '../../components/public/PublicLayout';
import {
  LOCATION_ADDRESS,
  MAPS_URL,
  PHONE_DISPLAY,
  SUPPORT_EMAIL,
  WHATSAPP_NUMBER
} from '../../publicSite/config';
import { usePageMeta } from '../../publicSite/usePageMeta';

const initialForm = { name: '', email: '', phone: '', subject: '', message: '' };

const validate = (form) => {
  const next = {};
  if (!form.name.trim()) next.name = 'Enter your name.';
  if (!/\S+@\S+\.\S+/.test(form.email)) next.email = 'Enter a valid email address.';
  if (!form.phone.trim()) next.phone = 'Enter your phone number.';
  if (!form.subject.trim()) next.subject = 'Enter an enquiry subject.';
  if (!form.message.trim()) next.message = 'Tell us how we can help.';
  return next;
};

const readStoredSubmissions = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('shem_contact') || '[]');
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
};

export default function Contact() {
  usePageMeta(
    'Contact School of Shem',
    'Contact School of Shem about admissions, academic faculties, partnerships, or portal support.'
  );
  const [form, setForm] = useState(initialForm);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const updateField = (field) => (event) => {
    setForm((previous) => ({ ...previous, [field]: event.target.value }));
    setErrors((previous) => ({ ...previous, [field]: '' }));
    setSuccess(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload = { ...form, submittedAt: new Date().toISOString() };
    localStorage.setItem('shem_contact', JSON.stringify([...readStoredSubmissions(), payload]));
    setSuccess(true);

    const whatsappNumber = String(WHATSAPP_NUMBER || '').replace(/\D/g, '');
    if (whatsappNumber) {
      const enquiry = [
        'School of Shem Contact Enquiry',
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        `Phone: ${form.phone}`,
        `Subject: ${form.subject}`,
        `Message: ${form.message}`
      ].join('\n');
      window.open(
        `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(enquiry)}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  return (
    <PublicLayout>
      <PageHero
        eyebrow="Contact"
        title="Get in touch"
        subtitle="Speak with the School of SHEM team about admissions, academic pathways, partnerships, or support."
        image="/images/shem-campus-hero.jpg"
        imageAlt="The School of SHEM learning community on campus"
      />

      <section className="ps-section">
        <div className="ps-section-inner public-contact-layout">
          <div>
            <div className="ps-hero-eyebrow">School of SHEM</div>
            <h2 className="ps-section-title">We are here to help.</h2>
            <p className="ps-section-copy">
              Send an enquiry using the form, or contact the team directly using the verified details below.
            </p>
            <dl className="public-contact-details">
              <div>
                <dt>Email</dt>
                <dd><a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></dd>
              </div>
              <div>
                <dt>Phone and WhatsApp</dt>
                <dd><a href={`https://wa.me/${WHATSAPP_NUMBER}`}>{PHONE_DISPLAY}</a></dd>
              </div>
              <div>
                <dt>Office</dt>
                <dd><a href={MAPS_URL} target="_blank" rel="noreferrer">{LOCATION_ADDRESS}</a></dd>
              </div>
              <div>
                <dt>Office hours</dt>
                <dd>Monday–Friday, 09:00–17:00 SAST</dd>
              </div>
            </dl>
          </div>

          <div className="public-enquiry-panel">
            <h2>Send an enquiry</h2>
            <p>All fields are required.</p>
            {success && (
              <div className="public-form-success" role="status">
                Your enquiry has been prepared successfully. Continue in WhatsApp to send it to the team.
              </div>
            )}
            <form className="public-form" onSubmit={handleSubmit} noValidate>
              <div className="public-form-grid">
                <label>
                  <span>Full name</span>
                  <input
                    name="name"
                    autoComplete="name"
                    value={form.name}
                    onChange={updateField('name')}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? 'contact-name-error' : undefined}
                  />
                  {errors.name && <small id="contact-name-error">{errors.name}</small>}
                </label>
                <label>
                  <span>Email address</span>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={updateField('email')}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'contact-email-error' : undefined}
                  />
                  {errors.email && <small id="contact-email-error">{errors.email}</small>}
                </label>
              </div>
              <div className="public-form-grid">
                <label>
                  <span>Phone / WhatsApp</span>
                  <input
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={updateField('phone')}
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={errors.phone ? 'contact-phone-error' : undefined}
                  />
                  {errors.phone && <small id="contact-phone-error">{errors.phone}</small>}
                </label>
                <label>
                  <span>Subject</span>
                  <input
                    name="subject"
                    value={form.subject}
                    onChange={updateField('subject')}
                    aria-invalid={Boolean(errors.subject)}
                    aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
                  />
                  {errors.subject && <small id="contact-subject-error">{errors.subject}</small>}
                </label>
              </div>
              <label>
                <span>Message</span>
                <textarea
                  name="message"
                  rows="6"
                  value={form.message}
                  onChange={updateField('message')}
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={errors.message ? 'contact-message-error' : undefined}
                />
                {errors.message && <small id="contact-message-error">{errors.message}</small>}
              </label>
              <button className="btn btn-primary btn-lg" type="submit">Submit Enquiry</button>
            </form>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
